export const FREE_TRY_LIMIT = 5;

const STORAGE_KEY = 'beautyiq_vto_access_v1';

export interface VtoAccessState {
  visitorId: string;
  used: number;
  limit: number;
  createdAt: string;
  updatedAt: string;
}

export interface ConsumeVtoTryResult {
  allowed: boolean;
  consumed: boolean;
  state: VtoAccessState;
}

function nowIso(): string {
  return new Date().toISOString();
}

function createVisitorId(): string {
  try {
    if (typeof crypto.randomUUID === 'function') {
      return `v_${crypto.randomUUID()}`;
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return `v_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

function createState(): VtoAccessState {
  const at = nowIso();
  return {
    visitorId: createVisitorId(),
    used: 0,
    limit: FREE_TRY_LIMIT,
    createdAt: at,
    updatedAt: at
  };
}

function normalizeState(value: unknown): VtoAccessState {
  const fallback = createState();
  if (!value || typeof value !== 'object') return fallback;

  const raw = value as Partial<VtoAccessState>;
  const used = Number.isFinite(raw.used) ? Math.max(0, Math.floor(Number(raw.used))) : 0;
  const limit = Number.isFinite(raw.limit)
    ? Math.max(1, Math.floor(Number(raw.limit)))
    : FREE_TRY_LIMIT;

  return {
    visitorId: typeof raw.visitorId === 'string' && raw.visitorId ? raw.visitorId : fallback.visitorId,
    used: Math.min(used, limit),
    limit,
    createdAt: typeof raw.createdAt === 'string' && raw.createdAt ? raw.createdAt : fallback.createdAt,
    updatedAt: typeof raw.updatedAt === 'string' && raw.updatedAt ? raw.updatedAt : fallback.updatedAt
  };
}

function saveState(state: VtoAccessState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // If storage is unavailable, the paywall degrades to per-session best effort.
  }
}

export function getVtoAccessState(): VtoAccessState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const state = normalizeState(raw ? JSON.parse(raw) : null);
    saveState(state);
    return state;
  } catch {
    const state = createState();
    saveState(state);
    return state;
  }
}

export function remainingFreeTries(state = getVtoAccessState()): number {
  void state;
  return Number.POSITIVE_INFINITY; // BeautyIQ is free — unlimited VTO tries
}

export function consumeVtoTry(): ConsumeVtoTryResult {
  // BeautyIQ is free — every VTO try is allowed and nothing is consumed/limited.
  // saveState/nowIso stay used by getVtoAccessState; the paywall path is dormant.
  return { allowed: true, consumed: false, state: getVtoAccessState() };
}
